import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import "./App.scss";
import HelpOverlay from "./components/HelpOverlay";
import Sidebar from "./components/Sidebar";
import Toolbar from "./components/Toolbar";
import NewSongPage from "./pages/NewSong";
import SongPage from "./pages/SongPage";
import TestPage from "./pages/TestPage";
import TexToLyricsPage from "./pages/TexToLyrics";
import EditorPage from "./pages/editor/EditorPage";
import SettingsPage from "./pages/settings/SettingsPage";
import model from "./resources/model/model";

//the ? shortcut must yield to a text field so it can still be typed there
function isTypingTarget() {
  const el = document.activeElement as HTMLElement | null;
  const tag = el?.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || !!el?.isContentEditable;
}

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const location = useLocation();
  useEffect(() => {
    model.onRouteChanged();
  }, [location]);

  const navigate = useNavigate()

  const [, foo] = useState({});
  const forceUpdate = useCallback(()=>foo({}), [])
  useEffect(() => {
    model.addUpdateCallback(() => forceUpdate());
    model.setState({navigate: (to: string)=>navigate(to)});
  },[forceUpdate, navigate])

  //the auto fit button has to notice when a resize (a phone turned, the
  //sidebar opened) leaves the fit made for another page width
  useEffect(() => {
    const main = document.getElementById("main");
    if (!main || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => model.onMainResized());
    observer.observe(main);
    return () => observer.disconnect();
  }, []);

  //ctrl+f opens the song search, ? toggles the shortcut help, escape closes
  //either - keyboard-only browsing without reaching for the mouse
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setSidebarOpen(true);
        requestAnimationFrame(() => {
          const el = document.getElementById("sidebar-search") as HTMLInputElement | null;
          el?.focus();
          el?.select();
        });
      } else if (e.key === "?" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget()) {
        e.preventDefault();
        setHelpOpen((h) => !h);
      } else if (e.key === "Escape") {
        setHelpOpen(false);
        setSidebarOpen(false);
        const el = document.getElementById("sidebar-search");
        if (document.activeElement === el) (el as HTMLElement)?.blur();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [])

  const settings = model.settings;
  let className = settings.darkMode ? "dark-theme" : "light-theme"
  className = className + (settings.oledMode && settings.darkMode ? " oled-theme":"")
  className = className + (settings.compactMode ? " compactMode" : "")

  
  return (
    <div id="app" className={className}>
      <Sidebar toggle={() => setSidebarOpen(!sidebarOpen)} open={sidebarOpen} />

      <div id="main">
        <Routes>
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/editor" element={<EditorPage/>}/>
          <Route path="/editor/:id" element={<EditorPage/>}/>
          <Route path="/new" element={<NewSongPage />} />
          <Route path="/ttl" element={<TexToLyricsPage />} />
          <Route path="/test" element={<TestPage />} />
          <Route path="/:id" element={<SongPage />} />
          <Route path="/" element={<Navigate to="/li7g0fr0z3n1d1n" />}></Route>
        </Routes>
      </div>

      <Toolbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} toggleHelp={() => setHelpOpen((h) => !h)} />

      <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}

export default App;
